import { useCallback, useEffect, useMemo, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import CyclePDFDocument from "./CyclePDFDocument";

const buttonClass = "pd-btn pd-btn--ghost";
const fileName = "cycle_spaces_results.pdf";

const serialize = (value) => {
  if (value === undefined) return null;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (err) {
    console.warn("[cycles] Failed to serialise data for PDF export", err);
    return null;
  }
};

export default function DownloadCycleReportBtn({ project, result, disabled }) {
  const serializedProject = useMemo(() => serialize(project), [project]);
  const serializedResult = useMemo(() => serialize(result), [result]);

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");

  const hasResults = useMemo(
    () =>
      serializedResult &&
      typeof serializedResult === "object" &&
      serializedResult.requirements &&
      typeof serializedResult.requirements === "object" &&
      serializedResult.requirements.long_stay_spaces != null,
    [serializedResult],
  );

  const exportReady = useMemo(
    () => Boolean(serializedProject && serializedResult && hasResults),
    [hasResults, serializedProject, serializedResult],
  );

  useEffect(() => {
    if (exportReady) setError("");
  }, [exportReady]);

  const handleDownload = useCallback(async () => {
    if (!exportReady || disabled || isGenerating) return;

    setIsGenerating(true);
    setError("");

    try {
      const blob = await pdf(
        <CyclePDFDocument project={serializedProject} result={serializedResult} />,
        {},
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("[cycles] PDF export failed", err);
      setError(err?.message || "Export unavailable");
    } finally {
      setIsGenerating(false);
    }
  }, [disabled, exportReady, isGenerating, serializedProject, serializedResult]);

  const disabledState = disabled || isGenerating || !exportReady;
  const title = error || (!exportReady ? "Run the calculator to export." : "");

  return (
    <button
      type="button"
      className={buttonClass}
      onClick={handleDownload}
      disabled={disabledState}
      aria-disabled={disabledState}
      title={title || undefined}
    >
      {isGenerating ? "Generating…" : "Export PDF"}
    </button>
  );
}
